import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  fetchTargetedFeedbackForms,
  type FeedbackFormSummary,
  type SectionFormEntry,
} from "../lib/feedback-api";

export function useFeedbackForms() {
  const location = useLocation();
  const [forms, setForms] = useState<FeedbackFormSummary[]>([]);
  const [sectionForms, setSectionForms] = useState<Map<string, SectionFormEntry>>(new Map());
  const dismissedRef = useRef(new Set<number>());

  useEffect(() => {
    const controller = new AbortController();

    fetchTargetedFeedbackForms(location.pathname, controller.signal)
      .then(({ forms: loaded, sectionForms: loadedSection }) => {
        if (controller.signal.aborted) return;
        const visible = loaded.filter(
          (f) => f.targeting.triggerMode !== "button_only" && !dismissedRef.current.has(f.id)
        );
        for (const f of visible) {
          if (f.targeting.triggerMode === "once_per_session") {
            dismissedRef.current.add(f.id);
          }
        }
        setForms(visible);
        setSectionForms(new Map(loadedSection.map((entry) => [entry.section, entry])));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setForms([]);
        setSectionForms(new Map());
      });

    return () => {
      controller.abort();
    };
  }, [location.pathname]);

  const dismissForm = useCallback((formId: number) => {
    dismissedRef.current.add(formId);
    setForms((prev) => prev.filter((f) => f.id !== formId));
  }, []);

  return { forms, sectionForms, dismissForm };
}
